import { Menu, Settings, User, LogOut, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import wealthyStewardLogo from "@/assets/wealthy-steward-logo.png";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { InstallAppMenuItem } from "@/components/pwa/InstallAppMenuItem";

interface MobileHeaderProps {
  journeyCompleted: boolean;
  isAdmin: boolean;
}

export function MobileHeader({ journeyCompleted, isAdmin }: MobileHeaderProps) {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  const getInitials = () => {
    if (!user?.email) return "U";
    return user.email.substring(0, 2).toUpperCase();
  };

  return (
    <header className="sticky top-0 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60 border-b z-40 safe-area-pt" style={{ paddingLeft: 'env(safe-area-inset-left)', paddingRight: 'env(safe-area-inset-right)' }}>
      <div className="flex items-center justify-between h-14 px-4">
        {/* Menu Button */}
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="min-w-[44px] min-h-[44px]">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[280px] p-0">
            <div className="flex flex-col h-full">
              {/* Header in sheet */}
              <div className="p-4 border-b">
                <img src={wealthyStewardLogo} alt="Wealthy Steward" className="h-10 w-auto" />
              </div>
              
              {/* Navigation */}
              <nav className="flex flex-col gap-1 p-4 flex-1">
                {!journeyCompleted && (
                  <button
                    onClick={() => navigate("/journey")}
                    className="text-left p-3 hover:bg-muted rounded-lg transition-colors font-medium"
                  >
                    My Journey
                  </button>
                )}
                <button
                  onClick={() => navigate("/dashboard")}
                  className="text-left p-3 hover:bg-muted rounded-lg transition-colors font-medium"
                >
                  Dashboard
                </button>
                <button
                  onClick={() => navigate("/governance")}
                  className="text-left p-3 hover:bg-muted rounded-lg transition-colors font-medium"
                >
                  Governance
                </button>
                <button
                  onClick={() => navigate("/support")}
                  className="text-left p-3 hover:bg-muted rounded-lg transition-colors font-medium"
                >
                  Support
                </button>
                
              </nav>
              
              {/* Bottom section */}
              <div className="p-4 border-t">
                <button
                  onClick={() => navigate("/settings")}
                  className="flex items-center gap-2 w-full p-3 hover:bg-muted rounded-lg transition-colors"
                >
                  <Settings className="h-4 w-4" />
                  <span className="font-medium">Settings</span>
                </button>
                {isAdmin && (
                  <button
                    onClick={() => navigate("/admin/dashboard")}
                    className="flex items-center gap-2 w-full p-3 hover:bg-muted rounded-lg transition-colors"
                  >
                    <Shield className="h-4 w-4" />
                    <span className="font-medium">Admin Portal</span>
                  </button>
                )}
                <button
                  onClick={signOut}
                  className="flex items-center gap-2 w-full p-3 hover:bg-muted rounded-lg transition-colors text-destructive"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="font-medium">Sign Out</span>
                </button>
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {/* Logo */}
        <Link to="/journey" className="flex items-center flex-shrink-0">
          <img src={wealthyStewardLogo} alt="Wealthy Steward" className="h-8 object-contain" style={{ aspectRatio: 'auto' }} />
        </Link>

        {/* Right side: notifications + avatar */}
        <div className="flex items-center gap-1">
          <NotificationBell />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="min-w-[44px] min-h-[44px]">
            <div className="w-8 h-8 rounded-full bg-gold text-charcoal flex items-center justify-center text-sm font-medium">
                {getInitials()}
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">My Account</p>
                <p className="text-xs leading-none text-muted-foreground">
                  {user?.email}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/settings" className="cursor-pointer">
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </Link>
            </DropdownMenuItem>
            {isAdmin && (
              <DropdownMenuItem asChild>
                <Link to="/admin/dashboard" className="cursor-pointer">
                  <Shield className="mr-2 h-4 w-4" />
                  Admin Portal
                </Link>
              </DropdownMenuItem>
            )}
            <InstallAppMenuItem />
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={signOut} className="cursor-pointer text-destructive">
              <LogOut className="mr-2 h-4 w-4" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
      </div>
    </header>
  );
}